import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useEffect } from "react";
import {
  $getRoot,
  $isParagraphNode,
  $createParagraphNode,
  PASTE_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
} from "lexical";
import { $isTableNode } from "../nodes/CustomTableNode/src";
import { ColoredNode } from "../nodes/TableNode";
import { generateStableCaptionId } from "./TablePlugin";

export function TableCaptionOnPastePlugin({
  styleConfig = {
    fontColor: "black",
    fontSize: "12",
    bold: "No",
    italic: "No",
    alignment: "left",
    fontFamily: "Times New Roman",
    leadingSpace: "0",
    lineSpacing: "1",
    trailingSpace: "0",
  },
}: { styleConfig?: any }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const removePasteHandler = editor.registerCommand(
      PASTE_COMMAND,
      (event: ClipboardEvent) => {
        const clipboardData = event?.clipboardData;
        if (!clipboardData) return false;

        const html = clipboardData.getData("text/html");
        if (!html) return false;

        if (!html.toLowerCase().includes("<table")) {
          // No tables in paste, ignore
          return false;
        }

        setTimeout(() => {
          editor.update(() => {
            const root = $getRoot();
            const tables = root.getChildren().filter((node: any) => node.__isCaption && $isTableNode(node));

            // Go through all tables - only add caption if missing
            tables.forEach((tableNode, idx) => {
              const prevSibling = tableNode.getPreviousSibling();
              if (
                !(prevSibling && prevSibling.getTextContent().startsWith("Table"))
              ) {
                // Create caption paragraph with ColoredNode label similar to your InitialTableCaptionPlugin
                const captionText = `Table ${idx + 1}:`;
                const captionId = generateStableCaptionId(captionText, idx);
                const captionParagraph = $createParagraphNode();

                // Create ColoredNode label
                const labelNode = new ColoredNode(
                  captionText,
                  styleConfig.fontColor || "black",
                  `${styleConfig.fontSize}px`,
                  styleConfig.bold === "Yes" ? "bold" : "normal",
                  styleConfig.italic === "Yes" ? "italic" : "normal",
                  styleConfig.alignment || "left",
                  styleConfig.fontFamily || "Times New Roman",
                  styleConfig.leadingSpace || "0",
                  styleConfig.lineSpacing || "1",
                  styleConfig.trailingSpace || "0",
                  captionId
                );
                labelNode.setStyle("margin-right: 8px;");

                captionParagraph.append(labelNode);

                // Insert caption before the table
                tableNode.insertBefore(captionParagraph);
              }
            });
          });
        }, 0);

        return false; // Let Lexical handle native paste
      },
      COMMAND_PRIORITY_CRITICAL
    );

    return () => {
      removePasteHandler();
    };
  }, [editor, styleConfig]);

  return null;
}
